import '../assets/NcDialog-bP7Jt0ZE.css';
import { useElementSize } from "@vueuse/core";
import { defineComponent, ref, computed, resolveComponent, createBlock, createCommentVNode, openBlock, mergeProps, withCtx, createElementVNode, toDisplayString, resolveDynamicComponent, toHandlers, normalizeClass, createElementBlock, renderSlot, Fragment, renderList } from "vue";
import { N as NcModal } from "./NcModal-BisVxXe_.mjs";
import { _ as _sfc_main$1 } from "./NcDialogButton.vue_vue_type_script_setup_true_lang-DAZ2Budh.mjs";
import { c as createElementId } from "./createElementId-DhjFt1I9.mjs";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
const _sfc_main = defineComponent({
  name: "NcDialog",
  components: {
    NcDialogButton: _sfc_main$1,
    NcModal
  },
  props: {
    /** Name of the dialog (the heading) */
    name: {
      type: String,
      required: true
    },
    /** Text of the dialog */
    message: {
      type: String,
      default: ""
    },
    /** Additional elements to add to the focus trap */
    additionalTrapElements: {
      type: Array,
      validator: (arr) => {
        return Array.isArray(arr) && arr.every(
          (element) => typeof element === "string" || element instanceof HTMLElement
        );
      },
      default: () => []
    },
    /**
     * The element where to mount the dialog, if `null` is passed the dialog is mounted in place
     * @default 'body'
     */
    container: {
      type: String,
      required: false,
      default: "body"
    },
    /**
     * Whether the dialog should be shown
     * @default true
     */
    open: {
      type: Boolean,
      default: true
    },
    /**
     * Size of the underlying NcModal
     * @default 'small'
     * @type {'small'|'normal'|'large'|'full'}
     */
    size: {
      type: String,
      required: false,
      default: "small",
      validator: (value) => ["small", "normal", "large", "full"].includes(value)
    },
    /**
     * Buttons to display
     * @default []
     */
    buttons: {
      type: Array,
      required: false,
      default: () => [],
      validator(value) {
        return Array.isArray(value) && value.every((element) => typeof element === "object");
      }
    },
    /**
     * Do not show the close button for the dialog.
     * @default false
     */
    noClose: {
      type: Boolean,
      default: false
    },
    /**
     * Close the dialog if the user clicked outside of the dialog
     * Only relevant if `noClose` is not set.
     */
    closeOnClickOutside: {
      type: Boolean,
      default: false
    },
    /**
     * Make the dialog wrapper a HTML form element.
     * The buttons will be wrapped within the form so they can be used as submit / reset buttons.
     * Please note that when using the property the `navigation` should not be used.
     */
    isForm: {
      type: Boolean,
      default: false
    },
    /**
     * Declare if hiding the modal should be animated
     * @default false
     */
    outTransition: {
      type: Boolean,
      default: false
    },
    /**
     * Optionally pass additional classes which will be set on the navigation for custom styling
     * @default ''
     * @example
     * ```html
     * <DialogBase :navigation-classes="['mydialog-navigation']"><!-- --></DialogBase>
     * <!-- ... -->
     * <style lang="scss">
     * :deep(.mydialog-navigation) {
     *     flex-direction: row-reverse;
     * }
     * </style>
     * ```
     */
    navigationClasses: {
      type: [String, Array, Object],
      required: false,
      default: ""
    },
    /**
     * aria-label for the dialog navigation.
     * Use it when you want to provide a more meaningful label than the dialog name.
     *
     * By default, navigation is labeled by the dialog name.
     */
    navigationAriaLabel: {
      type: String,
      required: false,
      default: ""
    },
    /**
     * aria-labelledby for the dialog navigation.
     * Use it when you have an implicit navigation label (e.g. a heading).
     *
     * By default, navigation is labeled by the dialog name.
     */
    navigationAriaLabelledby: {
      type: String,
      required: false,
      default: ""
    },
    /**
     * Optionally pass additional classes which will be set on the content wrapper for custom styling
     * @default ''
     */
    contentClasses: {
      type: [String, Array, Object],
      required: false,
      default: ""
    },
    /**
     * Optionally pass additional classes which will be set on the dialog itself
     * (the default `class` attribute will be set on the modal wrapper)
     * @default ''
     */
    dialogClasses: {
      type: [String, Array, Object],
      required: false,
      default: ""
    }
  },
  emits: ["closing", "update:open", "reset", "submit"],
  setup(props, { emit, slots }) {
    const wrapper = ref();
    const { width: dialogWidth } = useElementSize(wrapper, { width: 900, height: 0 });
    const isNavigationCollapsed = computed(() => dialogWidth.value < 876);
    const hasNavigation = computed(() => slots?.navigation !== void 0);
    const navigationId = createElementId();
    const navigationAriaLabelAttr = computed(() => props.navigationAriaLabel || void 0);
    const navigationAriaLabelledbyAttr = computed(() => {
      if (props.navigationAriaLabel) {
        return void 0;
      }
      return props.navigationAriaLabelledby || navigationId;
    });
    const dialogElement = ref();
    const dialogTagName = computed(() => props.isForm && !hasNavigation.value ? "form" : "div");
    const dialogListeners = computed(
      () => dialogTagName.value === "form" ? {
        /**
         * @param {SubmitEvent} event Form submit event
         */
        submit(event) {
          event.preventDefault();
          emit("submit", event);
        },
        /**
         * @param {Event} event Form submit event
         */
        reset(event) {
          event.preventDefault();
          emit("reset", event);
        }
      } : {}
    );
    const showModal = ref(true);
    function handleButtonClose(button, result) {
      if (button.nativeType === "submit" && dialogTagName.value === "form" && !dialogElement.value.reportValidity()) {
        return;
      }
      handleClosing(result);
      window.setTimeout(() => handleClosed(), 300);
    }
    function handleClosing(result) {
      showModal.value = false;
      emit("closing", result);
    }
    const handleClosed = () => {
      showModal.value = true;
      emit("update:open", false);
    };
    const modalProps = computed(() => ({
      noClose: props.noClose,
      container: props.container === void 0 ? "body" : props.container,
      // we do not pass the name as we already have the name as the headline
      // name: props.name,
      // But we need to set the correct label id so the dialog is labelled
      labelId: navigationId,
      size: props.size,
      show: props.open && showModal.value,
      outTransition: props.outTransition,
      closeOnClickOutside: props.closeOnClickOutside,
      additionalTrapElements: props.additionalTrapElements
    }));
    return {
      dialogElement,
      dialogListeners,
      dialogTagName,
      handleButtonClose,
      handleClosing,
      handleClosed,
      hasNavigation,
      navigationId,
      navigationAriaLabelAttr,
      navigationAriaLabelledbyAttr,
      isNavigationCollapsed,
      modalProps,
      wrapper
    };
  }
});
const _hoisted_1 = ["id", "textContent"];
const _hoisted_2 = ["aria-label", "aria-labelledby"];
const _hoisted_3 = { class: "dialog__text" };
const _hoisted_4 = { class: "dialog__actions" };
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_NcDialogButton = resolveComponent("NcDialogButton");
  const _component_NcModal = resolveComponent("NcModal");
  return _ctx.open ? (openBlock(), createBlock(_component_NcModal, mergeProps({
    key: 0,
    class: "dialog__modal",
    "enable-slideshow": false,
    "disable-swipe": ""
  }, _ctx.modalProps, {
    onClose: _ctx.handleClosed,
    "onUpdate:show": _cache[0] || (_cache[0] = ($event) => _ctx.handleClosing())
  }), {
    default: withCtx(() => [
      createElementVNode("h2", {
        id: _ctx.navigationId,
        class: "dialog__name",
        textContent: toDisplayString(_ctx.name)
      }, null, 8, _hoisted_1),
      (openBlock(), createBlock(resolveDynamicComponent(_ctx.dialogTagName), mergeProps({
        ref: "dialogElement",
        class: ["dialog", _ctx.dialogClasses]
      }, toHandlers(_ctx.dialogListeners)), {
        default: withCtx(() => [
          createElementVNode("div", {
            ref: "wrapper",
            class: normalizeClass(["dialog__wrapper", { "dialog__wrapper--collapsed": _ctx.isNavigationCollapsed }])
          }, [
            _ctx.hasNavigation ? (openBlock(), createElementBlock("nav", {
              key: 0,
              class: normalizeClass(["dialog__navigation", _ctx.navigationClasses]),
              "aria-label": _ctx.navigationAriaLabelAttr,
              "aria-labelledby": _ctx.navigationAriaLabelledbyAttr
            }, [
              renderSlot(_ctx.$slots, "navigation", { isCollapsed: _ctx.isNavigationCollapsed }, void 0, true)
            ], 10, _hoisted_2)) : createCommentVNode("", true),
            createElementVNode("div", {
              class: normalizeClass(["dialog__content", _ctx.contentClasses])
            }, [
              renderSlot(_ctx.$slots, "default", {}, () => [
                createElementVNode("p", _hoisted_3, toDisplayString(_ctx.message), 1)
              ], true)
            ], 2)
          ], 2),
          createElementVNode("div", _hoisted_4, [
            renderSlot(_ctx.$slots, "actions", {}, () => [
              (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.buttons, (button, idx) => {
                return openBlock(), createBlock(_component_NcDialogButton, mergeProps({
                  key: idx,
                  ref_for: true
                }, button, {
                  onClick: (_, result) => _ctx.handleButtonClose(button, result)
                }), null, 16, ["onClick"]);
              }), 128))
            ], true)
          ])
        ]),
        _: 3
      }, 16, ["class"]))
    ]),
    _: 3
  }, 16, ["onClose"])) : createCommentVNode("", true);
}
const NcDialog = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-3709d2d3"]]);
export {
  NcDialog as N
};
//# sourceMappingURL=NcDialog-BQNR_-Ju.mjs.map
